import type { SearchableItem } from '@app/components/Guide/types/guide.types'
import { SearchInput } from '@app/components/ui/Search/input.search'
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover'
import { appLink } from '@app/config/appLink'
import { useDebounce } from '@app/hooks/useDebounce'
import { highlightMatch } from '@app/utils/highlightMatch'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGuideSearch } from '@app/components/Guide/hooks/useGuideSearch'

export const GuideSearch = () => {
  const navigate = useNavigate()
  const [inputValue, setInputValue] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const debounced = useDebounce(inputValue, 300)
  const { data, isLoading } = useGuideSearch(debounced)
  const resultRefs = useRef<Array<HTMLButtonElement | null>>([])

  const hasSearchQuery = debounced.trim().length >= 3
  const hasResults = !!data?.length
  const isPopoverOpen = isSearchOpen || (hasSearchQuery && hasResults)

  const onSelectItem = (item: SearchableItem) => {
    if (item.type === 'glossary') {
      navigate(`${appLink.guidePage.path}/glossary#${item.slug}`)
      setInputValue('')
      setIsSearchOpen(false)
      return
    }

    const slug = item.slug
    const subTab = item.sub_tabs?.[0]
    const mainTopic = subTab?.main_topic?.slug
    const subTopic = subTab?.slug
    if (!slug || !mainTopic || !subTopic) return
    navigate(`${appLink.guidePage.path}/${mainTopic}/${subTopic}/${slug}`)
    setInputValue('')
    setIsSearchOpen(false)
  }

  const renderSearchItem = (item: SearchableItem) => {
    if (item.type === 'glossary') {
      return {
        label: 'Glossary',
        title: item.term,
        description: item.definition,
      }
    }

    const subTab = item.sub_tabs?.[0]
    const mainTopic = subTab?.main_topic?.title
    const subTopic = subTab?.title

    return {
      label: [mainTopic, subTopic].filter(Boolean).join(' · '),
      title: item.title ?? '',
      description: item.description,
    }
  }

  const focusResult = (index: number) => {
    resultRefs.current[index]?.focus()
  }

  const handleResultKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!data?.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusResult(Math.min(index + 1, data.length - 1))
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      focusResult(Math.max(index - 1, 0))
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      setIsSearchOpen(false)
    }
  }

  return (
    <Popover open={isPopoverOpen}>
      <PopoverTrigger asChild>
        <div className="w-full">
          <SearchInput<SearchableItem>
            value={inputValue}
            onChange={(value) => {
              setInputValue(value)
              setIsSearchOpen(true)
            }}
            items={[]}
            isLoading={isLoading}
            minLength={3}
            placeholder="Search Guide"
            shortcutLabel="⌘K"
            onShortcut={() => setIsSearchOpen(true)}
            onInputKeyDown={(event) => {
              if (event.key === 'Escape') {
                setIsSearchOpen(false)
                return
              }

              if (event.key !== 'Tab' || event.shiftKey || !hasResults) return

              event.preventDefault()
              setIsSearchOpen(true)
              requestAnimationFrame(() => focusResult(0))
            }}
            className="h-auto rounded-lg border border-border bg-input py-2.5 pl-3.5 pr-2.5 text-base leading-6 shadow-none"
            inputClassName="text-base font-normal text-foreground placeholder:text-muted-foreground"
            iconClassName="h-5 w-5 text-muted-foreground"
            size="small"
            getItemKey={(item) => `${item.type}-${item.documentId}`}
            onSelectItem={onSelectItem}
            renderItem={({ item, query }) => {
              if (item.type === 'glossary') {
                return (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">Glossary</span>
                    <span className="truncate text-sm font-medium" title={item.term}>
                      {highlightMatch(item.term, query)}
                    </span>
                    <span
                      className="truncate text-xs text-muted-foreground"
                      title={item.definition}
                    >
                      {highlightMatch(item.definition, query)}
                    </span>
                  </div>
                )
              }

              const subTab = item.sub_tabs?.[0]
              const mainTopic = subTab?.main_topic?.title
              const subTopic = subTab?.title

              return (
                <div className="flex flex-col gap-0.5">
                  {(mainTopic || subTopic) && (
                    <span className="text-xs text-muted-foreground">
                      {mainTopic}
                      {mainTopic && subTopic && ' · '}
                      {subTopic}
                    </span>
                  )}
                  <span className="truncate text-sm font-medium" title={item.title ?? ''}>
                    {highlightMatch(item.title ?? '', query)}
                  </span>
                  {!!item.description && (
                    <span
                      className="truncate text-xs text-muted-foreground"
                      title={item.description}
                    >
                      {highlightMatch(item.description, query)}
                    </span>
                  )}
                </div>
              )
            }}
            onClear={() => {
              setInputValue('')
              setIsSearchOpen(false)
            }}
          />
        </div>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-72 overflow-hidden rounded-lg border border-border bg-card p-1 shadow-md"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex flex-col items-start">
          {!hasSearchQuery && (
            <div className="px-2.5 py-2 min-h-16 text-sm flex items-center justify-center text-muted-foreground">
              Start typing to search the Guide
            </div>
          )}

          {hasSearchQuery && isLoading && (
            <div className="px-2.5 py-2 text-sm text-muted-foreground">Searching...</div>
          )}

          {hasSearchQuery && !isLoading && !hasResults && (
            <div className="px-2.5 py-2 text-sm text-muted-foreground">No results found</div>
          )}

          {hasSearchQuery &&
            data?.map((item, index) => {
              const renderedItem = renderSearchItem(item)

              return (
                <button
                  key={`${item.type}-${item.documentId}`}
                  type="button"
                  ref={(element) => {
                    resultRefs.current[index] = element
                  }}
                  className="flex w-full items-center gap-2.5 overflow-hidden rounded-md px-2.5 py-1.5 text-left hover:bg-zinc-100 focus:bg-zinc-100 focus:outline-none"
                  onClick={() => onSelectItem(item)}
                  onKeyDown={(event) => handleResultKeyDown(event, index)}
                >
                  <span className="flex flex-col items-start gap-0.5 overflow-hidden">
                    {!!renderedItem.label && (
                      <span className="text-xs font-normal leading-5 text-muted-foreground">
                        {renderedItem.label}
                      </span>
                    )}
                    <span className="truncate text-sm font-normal leading-6 text-foreground">
                      {highlightMatch(renderedItem.title, inputValue)}
                    </span>
                  </span>
                </button>
              )
            })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
